import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  CheckCircle2, XCircle, Calendar, AlertTriangle, Clock, 
  Users, MapPin, ChevronLeft, X, UserPlus, Ban, Undo2, UserCog,
  Lock, RefreshCcw, Loader2
} from "lucide-react";
import { Sidebar } from "@/components/Navigation";
import BottomNav from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { getActiveStudioId } from "@/lib/api";
import { toast } from "sonner";
import { format, isToday, isBefore, addHours, parseISO } from "date-fns";
import { formatBatchSchedule } from "@/lib/batchFormat";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useConfirmDialog } from "@/components/ConfirmDialog";
import { ReceiptViewerModal } from "@/components/receipts/ReceiptViewerModal";

const AttendanceManagement = ({ user, onLogout }) => {
  const { confirm, dialog: confirmDialogEl } = useConfirmDialog();
  const [batches, setBatches] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  // Autosave state — one source of truth for the global header indicator.
  // saveState: "idle" | "saving" | "saved" | "failed"
  const [saveState, setSaveState] = useState("idle");
  const [lastSavedAt, setLastSavedAt] = useState(null);
  // Per-student error (so individual rows can show a retry chip without
  // blocking the rest of the list). Map<studentId, errorMsg>.
  const [rowErrors, setRowErrors] = useState({});
  
  // Cancel dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  
  // Add trial student dialog
  const [trialDialogOpen, setTrialDialogOpen] = useState(false);
  const [allStudents, setAllStudents] = useState([]);
  const [trialStudentId, setTrialStudentId] = useState("");
  const [trialStudentSearch, setTrialStudentSearch] = useState("");
  // Feb 2026 — drop-in / trial payment recording
  const [trialFee, setTrialFee] = useState("");
  const [trialPaymentStatus, setTrialPaymentStatus] = useState("paid"); // paid | partial | pending
  const [trialAmountPaid, setTrialAmountPaid] = useState("");
  const [trialPaymentMethod, setTrialPaymentMethod] = useState("cash");
  const [trialNotes, setTrialNotes] = useState("");
  const [trialSubmitting, setTrialSubmitting] = useState(false);
  const [studioTrialDefaultFee, setStudioTrialDefaultFee] = useState(0);
  // Receipt viewer for freshly-issued trial receipts
  const [receiptViewerOpen, setReceiptViewerOpen] = useState(false);
  const [issuedReceipt, setIssuedReceipt] = useState(null);

  // Substitute (change instructor) dialog
  const [substituteOpen, setSubstituteOpen] = useState(false);
  const [substituteForm, setSubstituteForm] = useState({ instructor_id: "", scope: "this_only", reason: "" });
  const [allInstructors, setAllInstructors] = useState([]);

  useEffect(() => {
    fetchBatches();
    api.get("/instructors").then(r => setAllInstructors(r.data?.instructors || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchSessionsForDate();
    }
  }, [selectedDate]);

  const fetchBatches = async () => {
    try {
      const response = await api.get("/batches");
      // Operational rule (Feb 2026): only ACTIVE batches surface in the
      // Attendance page selector. Inactive + archived stay out of the picker
      // while their historical sessions remain accessible by direct link.
      const all = response.data.batches || [];
      setBatches(all.filter((b) => !b.status || b.status === "active"));
    } catch (error) {
      toast.error("Failed to load batches");
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionsForDate = async () => {
    try {
      const response = await api.get(`/sessions?date=${selectedDate}`);
      setSessions(response.data.sessions);
    } catch (error) {
      console.error("Failed to load sessions");
    }
  };

  const generateSessionsForToday = async (batchId) => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const response = await api.post("/sessions/generate", null, {
        params: {
          batch_id: batchId,
          start_date: today,
          end_date: today
        }
      });
      toast.success(`Generated ${response.data.sessions_created} session(s)`);
      fetchSessionsForDate();
    } catch (error) {
      toast.error("Failed to generate sessions");
    }
  };

  const selectSession = async (session) => {
    try {
      setLoading(true);
      const response = await api.get(`/attendance/session/${session.session_id}`);
      setSessionData(response.data);
      setSelectedSession(session);
    } catch (error) {
      toast.error("Failed to load session data");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------
  // Helper: whether the current session is locked (completed). When locked
  // every attendance write is rejected by the backend; the UI mirrors that
  // by disabling toggles + showing a "Reopen session" affordance for admins.
  // ---------------------------------------------------------------------
  const isLocked = sessionData?.session?.status === "completed";
  const canReopen = user && (user.role === "super_admin" || user.role === "admin" || user.is_super_admin);

  const markAttendance = async (studentId, status, isTrial = false) => {
    if (!selectedSession) return;
    if (isLocked) {
      toast.error("Session is locked. Reopen it to edit attendance.");
      return;
    }

    setMarking(true);
    setSaveState("saving");
    setRowErrors((prev) => {
      if (!prev[studentId]) return prev;
      const next = { ...prev }; delete next[studentId]; return next;
    });

    try {
      const response = await api.post("/attendance", {
        session_id: selectedSession.session_id,
        student_id: studentId,
        status: status,
        is_trial: isTrial
      });

      // Update local state
      if (sessionData) {
        if (isTrial) {
          const updatedTrialStudents = sessionData.trial_students.map(s => {
            if (s.student.user_id === studentId) {
              return {
                ...s,
                is_marked: true,
                attendance_status: status,
                attendance: response.data.attendance
              };
            }
            return s;
          });
          setSessionData({
            ...sessionData,
            trial_students: updatedTrialStudents
          });
        } else {
          const updatedStudents = sessionData.students.map(s => {
            if (s.student.user_id === studentId) {
              return {
                ...s,
                is_marked: true,
                attendance_status: status,
                attendance: response.data.attendance
              };
            }
            return s;
          });
          
          const markedCount = updatedStudents.filter(s => s.is_marked).length;
          const presentCount = updatedStudents.filter(s => s.attendance_status === "present").length;
          const absentCount = updatedStudents.filter(s => s.attendance_status === "absent").length;
          
          setSessionData({
            ...sessionData,
            students: updatedStudents,
            counts: {
              ...sessionData.counts,
              present: presentCount,
              absent: absentCount,
              pending: sessionData.counts.total - markedCount
            }
          });
        }
      }

      if (response.data.warnings?.length > 0) {
        toast.warning(response.data.warnings[0].message, { duration: 5000 });
      }

      // Google-Docs-style "All changes saved" — single source of truth in
      // the session header. We deliberately no longer toast on every tap.
      setSaveState("saved");
      setLastSavedAt(new Date());
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to save";
      setSaveState("failed");
      setRowErrors((prev) => ({ ...prev, [studentId]: msg }));
      toast.error(msg);
    } finally {
      setMarking(false);
    }
  };

  const undoAttendance = async (studentId, isTrial = false) => {
    if (!selectedSession) return;
    if (isLocked) {
      toast.error("Session is locked. Reopen it to edit attendance.");
      return;
    }
    setSaveState("saving");
    try {
      await api.delete(`/attendance/${selectedSession.session_id}/${studentId}`);
      
      // Update local state
      if (sessionData) {
        if (isTrial) {
          const updatedTrialStudents = sessionData.trial_students.map(s => {
            if (s.student.user_id === studentId) {
              return {
                ...s,
                is_marked: false,
                attendance_status: null,
                attendance: null
              };
            }
            return s;
          });
          setSessionData({
            ...sessionData,
            trial_students: updatedTrialStudents
          });
        } else {
          const updatedStudents = sessionData.students.map(s => {
            if (s.student.user_id === studentId) {
              return {
                ...s,
                is_marked: false,
                attendance_status: null,
                attendance: null
              };
            }
            return s;
          });
          
          const markedCount = updatedStudents.filter(s => s.is_marked).length;
          const presentCount = updatedStudents.filter(s => s.attendance_status === "present").length;
          const absentCount = updatedStudents.filter(s => s.attendance_status === "absent").length;
          
          setSessionData({
            ...sessionData,
            students: updatedStudents,
            counts: {
              ...sessionData.counts,
              present: presentCount,
              absent: absentCount,
              pending: sessionData.counts.total - markedCount
            }
          });
        }
      }
      
      toast.success("Attendance removed");
      setSaveState("saved");
      setLastSavedAt(new Date());
    } catch (error) {
      setSaveState("failed");
      toast.error(error.response?.data?.detail || "Failed to undo attendance");
    }
  };

  const cancelSession = async () => {
    if (!selectedSession || !cancellationReason.trim()) {
      toast.error("Please provide a cancellation reason");
      return;
    }

    try {
      const response = await api.post(`/sessions/${selectedSession.session_id}/cancel`, {
        reason: cancellationReason
      });
      
      toast.success(response.data.message);
      setCancelDialogOpen(false);
      setCancellationReason("");
      
      // Go back to session list
      setSelectedSession(null);
      setSessionData(null);
      fetchSessionsForDate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel session");
    }
  };

  const openTrialDialog = async () => {
    try {
      const response = await api.get("/users?role=student");
      setAllStudents(response.data.users);
      // Prefill fee from studio.trial_default_fee (Feb 2026 drop-in payment)
      let defaultFee = 0;
      try {
        const studioId = selectedSession?.studio_id || getActiveStudioId();
        if (studioId) {
          const s = await api.get(`/studios/${studioId}`);
          defaultFee = Number(s.data?.studio?.trial_default_fee || 0);
        }
      } catch (_) {
        // Fee prefill is best-effort — dialog still opens if the studio call fails.
      }
      setStudioTrialDefaultFee(defaultFee);
      setTrialFee(defaultFee > 0 ? String(defaultFee) : "");
      setTrialPaymentStatus("paid");
      setTrialAmountPaid("");
      setTrialPaymentMethod("cash");
      setTrialNotes("");
      setTrialDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load students");
    }
  };

  const completeSession = async () => {
    if (!selectedSession) return;
    const ok = await confirm({
      title: "Mark session as complete?",
      message: "Once marked complete, attendance will be locked for payroll and reporting. The session can be reopened by an admin if needed.\n\nContinue?",
      confirmText: "Mark Complete",
      cancelText: "Cancel",
    });
    if (!ok) return;
    try {
      const res = await api.put(`/sessions/${selectedSession.session_id}/complete`);
      // Drive UI from the response — no follow-up refetch needed. This is
      // what fixes the original "no visible change" bug.
      if (res.data?.session && sessionData) {
        setSessionData({ ...sessionData, session: res.data.session });
      }
      toast.success("Attendance submitted successfully. Session marked as completed.");
      fetchSessionsForDate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete session");
    }
  };

  const reopenSession = async () => {
    if (!selectedSession) return;
    const ok = await confirm({
      title: "Reopen this session?",
      message: "Attendance will become editable again. This action is audit-logged.",
      confirmText: "Reopen",
      cancelText: "Cancel",
    });
    if (!ok) return;
    try {
      const res = await api.post(`/sessions/${selectedSession.session_id}/reopen`, {});
      if (res.data?.session && sessionData) {
        setSessionData({ ...sessionData, session: res.data.session });
      }
      toast.success("Session reopened. Attendance is now editable.");
      fetchSessionsForDate();
    } catch (error) {
      // Surface payroll-locked 409 with a follow-up confirm.
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.requires_ack === "acknowledge_payroll_locked") {
        const ack = await confirm({
          title: "Payroll month is finalised",
          message: `${detail.message}\n\nContinue anyway?`,
          confirmText: "Reopen anyway",
          cancelText: "Cancel",
          variant: "destructive",
        });
        if (!ack) return;
        try {
          const retry = await api.post(`/sessions/${selectedSession.session_id}/reopen`, { acknowledge_payroll_locked: true });
          if (retry.data?.session && sessionData) {
            setSessionData({ ...sessionData, session: retry.data.session });
          }
          toast.success("Session reopened. Payroll will need re-confirmation.");
          fetchSessionsForDate();
          return;
        } catch (e2) {
          toast.error(e2.response?.data?.detail || "Failed to reopen session");
          return;
        }
      }
      toast.error(typeof detail === "string" ? detail : "Failed to reopen session");
    }
  };

  const submitSubstitute = async () => {
    if (!substituteForm.instructor_id) { toast.error("Pick an instructor"); return; }
    try {
      await api.post(`/sessions/${selectedSession.session_id}/substitute`, {
        instructor_id: substituteForm.instructor_id,
        scope: substituteForm.scope,
        reason: substituteForm.reason || null,
      });
      toast.success(substituteForm.scope === "this_only" ? "Instructor changed for this session" : "Instructor updated for this and all future sessions");
      setSubstituteOpen(false);
      setSubstituteForm({ instructor_id: "", scope: "this_only", reason: "" });
      await selectSession(selectedSession);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change instructor");
    }
  };

  const addTrialStudent = async () => {
    if (!trialStudentId) return;

    // Validate trial payment fields (Feb 2026)
    const feeNum = trialFee === "" ? null : Number(trialFee);
    if (feeNum !== null && (Number.isNaN(feeNum) || feeNum < 0)) {
      toast.error("Enter a valid non-negative fee");
      return;
    }
    let amountPaidNum = null;
    if (trialPaymentStatus === "partial") {
      amountPaidNum = trialAmountPaid === "" ? NaN : Number(trialAmountPaid);
      if (Number.isNaN(amountPaidNum) || amountPaidNum <= 0) {
        toast.error("Enter amount paid for a partial payment");
        return;
      }
      if (feeNum !== null && amountPaidNum >= feeNum) {
        toast.error("Amount paid must be less than fee for partial payment");
        return;
      }
    }

    setTrialSubmitting(true);
    try {
      const payload = {
        session_id: selectedSession.session_id,
        student_id: trialStudentId,
        status: "present",
      };
      // Only send payment fields when instructor actually wants to record one.
      // Sending "pending" with amount=0 is a valid "no payment yet" record.
      if (feeNum !== null && feeNum > 0) {
        payload.amount = feeNum;
        payload.payment_status = trialPaymentStatus;
        payload.payment_method = trialPaymentMethod;
        if (trialPaymentStatus === "partial") {
          payload.amount_paid = amountPaidNum;
        }
        if (trialNotes.trim()) payload.notes = trialNotes.trim();
      }

      const res = await api.post("/attendance/trial", payload);

      toast.success("Trial student added");
      setTrialDialogOpen(false);
      setTrialStudentId("");
      setTrialStudentSearch("");

      // If receipt was generated, offer instant view
      if (res.data?.receipt) {
        setIssuedReceipt(res.data.receipt);
        setReceiptViewerOpen(true);
      }

      // Refresh session data
      selectSession(selectedSession);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add trial student");
    } finally {
      setTrialSubmitting(false);
    }
  };

  const getSubscriptionLabel = (status) => {
    switch (status) {
      case "expired":
        return { text: "Subscription expired", color: "text-red-600 bg-red-50" };
      case "expiring_soon":
        return { text: "Expiring soon", color: "text-orange-600 bg-orange-50" };
      default:
        return null;
    }
  };

  const formatSessionTime = (session) => {
    if (!session) return "";
    return `${session.start_time || ''} - ${session.end_time || ''}`;
  };

  const getSessionDateDisplay = (session) => {
    if (!session?.date) return "";
    const date = new Date(session.date);
    if (isToday(date)) return "Today";
    return format(date, "EEE, dd MMM");
  };

  // Filter out enrolled students from trial student list
  const availableTrialStudents = allStudents.filter(student => {
    if (!sessionData) return true;
    const isEnrolled = sessionData.students.some(s => s.student.user_id === student.user_id);
    const isAlreadyTrial = sessionData.trial_students?.some(t => t.student.user_id === student.user_id);
    return !isEnrolled && !isAlreadyTrial;
  });

  return (
    <div className="min-h-screen bg-slate-50 md:pl-64">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="p-4 md:p-8 pb-20 md:pb-8">
        {!selectedSession ? (
          // Session Selection View
          <>
            <div className="mb-6">
              <h1 className="text-2xl sm:text-4xl font-bold font-heading text-slate-900" data-testid="attendance-title">
                Attendance
              </h1>
              <p className="text-sm text-slate-600 mt-1">Select a class session to mark attendance</p>
            </div>

            {/* Date Selector */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 mb-6">
              <div className="flex items-center gap-4">
                <Calendar className="w-5 h-5 text-slate-400" />
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="h-12 rounded-xl max-w-xs"
                />
                <span className="text-slate-600">
                  {isToday(new Date(selectedDate)) ? "Today" : format(new Date(selectedDate), "EEEE, MMMM d, yyyy")}
                </span>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : sessions.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
                <div className="text-center">
                  <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600 mb-6">No sessions scheduled for this date</p>
                  
                  {batches.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-sm text-slate-500">Generate sessions for batches:</p>
                      <div className="flex flex-wrap justify-center gap-2">
                        {batches.slice(0, 5).map(batch => (
                          <Button
                            key={batch.batch_id}
                            onClick={() => generateSessionsForToday(batch.batch_id)}
                            variant="outline"
                            size="sm"
                            className="rounded-full"
                            title={`${formatBatchSchedule(batch)}${batch.instructor_name ? ` · ${batch.instructor_name}` : ""}`}
                            data-testid={`generate-sessions-batch-${batch.batch_id}`}
                          >
                            {batch.name}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <h2 className="text-lg font-medium text-slate-700">
                  {sessions.length} Session{sessions.length > 1 ? 's' : ''} Today
                </h2>
                
                {sessions.map((session, index) => {
                  const isCancelled = session.status === "cancelled";
                  
                  return (
                    <motion.div
                      key={session.session_id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`bg-white rounded-2xl p-6 shadow-sm border transition-all cursor-pointer ${
                        isCancelled 
                          ? "border-red-200 bg-red-50/50 opacity-60" 
                          : "border-slate-100 hover:shadow-lg hover:border-violet-200"
                      }`}
                      onClick={() => !isCancelled && selectSession(session)}
                      style={{ borderLeftWidth: '4px', borderLeftColor: session.batch?.color || '#7C3AED' }}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-lg sm:text-xl font-bold font-heading text-slate-900 truncate">
                              {session.batch?.name || 'Unknown Batch'}
                            </h3>
                            {isCancelled && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded-full text-xs font-medium">
                                Cancelled
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-2 text-sm text-slate-600 flex-wrap">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {formatSessionTime(session)}
                            </span>
                            {session.studio && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {session.studio.name}
                              </span>
                            )}
                          </div>
                          {isCancelled && session.cancellation_reason && (
                            <p className="text-sm text-red-600 mt-2">
                              Reason: {session.cancellation_reason}
                            </p>
                          )}
                        </div>
                        {!isCancelled && (
                          <Button className="rounded-full bg-primary w-full sm:w-auto flex-shrink-0">
                            Mark Attendance
                          </Button>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          // Attendance Marking View
          <>
            {/* Session Context Header */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 mb-6">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSelectedSession(null);
                    setSessionData(null);
                  }}
                  className="rounded-full"
                >
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  Back
                </Button>
                
                <div className="flex gap-2 items-center flex-wrap">
                  {/* Global autosave indicator — Google-Docs-style, single
                      source of truth so admins know taps are persisted. */}
                  <span
                    data-testid="attendance-save-indicator"
                    className="text-xs flex items-center gap-1 mr-1 select-none"
                  >
                    {saveState === "saving" && (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-500" />
                        <span className="text-slate-500">Saving…</span>
                      </>
                    )}
                    {saveState === "saved" && (
                      <>
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                        <span className="text-slate-500">
                          Saved{lastSavedAt ? ` · ${format(lastSavedAt, "h:mm a")}` : ""}
                        </span>
                      </>
                    )}
                    {saveState === "failed" && (
                      <>
                        <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                        <span className="text-red-600">Save failed</span>
                      </>
                    )}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setSubstituteOpen(true)}
                    className="rounded-full text-xs sm:text-sm"
                    disabled={sessionData?.is_cancelled || isLocked}
                    size="sm"
                    data-testid="open-substitute-btn"
                  >
                    <UserCog className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Change Instructor</span>
                  </Button>
                  <Button
                    variant="outline"
                    onClick={openTrialDialog}
                    className="rounded-full text-xs sm:text-sm"
                    disabled={sessionData?.is_cancelled || isLocked}
                    size="sm"
                  >
                    <UserPlus className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Add Trial</span>
                  </Button>
                  {isLocked ? (
                    canReopen && (
                      <Button
                        variant="outline"
                        onClick={reopenSession}
                        className="rounded-full text-amber-700 border-amber-200 hover:bg-amber-50 text-xs sm:text-sm"
                        size="sm"
                        data-testid="reopen-session-btn"
                      >
                        <RefreshCcw className="w-4 h-4 sm:mr-1" />
                        <span className="hidden sm:inline">Reopen Session</span>
                      </Button>
                    )
                  ) : (
                    <Button
                      variant="outline"
                      onClick={completeSession}
                      className="rounded-full text-emerald-700 border-emerald-200 hover:bg-emerald-50 text-xs sm:text-sm"
                      disabled={sessionData?.is_cancelled}
                      size="sm"
                      data-testid="mark-session-complete"
                    >
                      <CheckCircle2 className="w-4 h-4 sm:mr-1" />
                      <span className="hidden sm:inline">Mark Complete</span>
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => setCancelDialogOpen(true)}
                    className="rounded-full text-red-600 border-red-200 hover:bg-red-50 text-xs sm:text-sm"
                    disabled={sessionData?.is_cancelled || isLocked}
                    size="sm"
                  >
                    <Ban className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Cancel Class</span>
                  </Button>
                </div>
              </div>

              {/* Completion banner — strong visible affordance */}
              {isLocked && (
                <div
                  data-testid="session-completed-banner"
                  className="mt-3 flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-sm text-emerald-800"
                >
                  <Lock className="w-4 h-4 text-emerald-600" />
                  <span>
                    <strong>Completed</strong>
                    {sessionData?.session?.completed_at && (
                      <> · {format(parseISO(sessionData.session.completed_at), "d MMM yyyy, h:mm a")}</>
                    )}
                    {" "}— attendance is locked for payroll and reporting.
                  </span>
                </div>
              )}
              
              {/* Session Info Banner */}
              <div 
                className="mt-4 p-4 rounded-xl"
                style={{ 
                  backgroundColor: `${sessionData?.session?.batch?.color || '#7C3AED'}15`,
                  borderLeft: `4px solid ${sessionData?.session?.batch?.color || '#7C3AED'}`
                }}
              >
                <h2 className="text-2xl font-bold font-heading text-slate-900">
                  {sessionData?.session?.batch?.name}
                </h2>
                <div className="flex flex-wrap items-center gap-4 mt-2 text-slate-600">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {getSessionDateDisplay(sessionData?.session)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {formatSessionTime(sessionData?.session)}
                  </span>
                  {sessionData?.session?.studio && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {sessionData?.session?.studio?.name}
                      {sessionData?.session?.room_id && sessionData?.session?.studio?.rooms && (
                        <> · {sessionData.session.studio.rooms.find(r => r.room_id === sessionData.session.room_id)?.name}</>
                      )}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Auto-save indicator */}
              <p className="text-xs text-slate-500 mt-3 text-center">
                ✓ Attendance auto-saved on tap
                {!sessionData?.is_editable && (
                  <span className="text-red-500 ml-2">• Session is locked (read-only)</span>
                )}
              </p>
            </div>

            {/* Cancelled Session Warning */}
            {sessionData?.is_cancelled && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6">
                <div className="flex items-center gap-2 text-red-700">
                  <Ban className="w-5 h-5" />
                  <span className="font-medium">This class has been cancelled</span>
                </div>
                {sessionData?.session?.cancellation_reason && (
                  <p className="text-red-600 mt-1">Reason: {sessionData.session.cancellation_reason}</p>
                )}
              </div>
            )}

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : (
              <>
                {/* Summary */}
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 mb-6">
                  <div className="flex items-center justify-around text-center">
                    <div>
                      <p className="text-3xl font-bold font-heading text-green-600">
                        {sessionData?.counts?.present || 0}
                      </p>
                      <p className="text-sm text-slate-600">Present</p>
                    </div>
                    <div className="w-px h-12 bg-slate-200"></div>
                    <div>
                      <p className="text-3xl font-bold font-heading text-red-600">
                        {sessionData?.counts?.absent || 0}
                      </p>
                      <p className="text-sm text-slate-600">Absent</p>
                    </div>
                    <div className="w-px h-12 bg-slate-200"></div>
                    <div>
                      <p className="text-3xl font-bold font-heading text-slate-400">
                        {sessionData?.counts?.pending || 0}
                      </p>
                      <p className="text-sm text-slate-600">Pending</p>
                    </div>
                    {sessionData?.counts?.trial > 0 && (
                      <>
                        <div className="w-px h-12 bg-slate-200"></div>
                        <div>
                          <p className="text-3xl font-bold font-heading text-blue-600">
                            {sessionData?.counts?.trial}
                          </p>
                          <p className="text-sm text-slate-600">Trial</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Enrolled Students */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-6">
                  <h3 className="text-lg font-bold font-heading text-slate-900 mb-4">
                    Enrolled Students ({sessionData?.students?.length || 0})
                  </h3>
                  
                  {sessionData?.students?.length === 0 ? (
                    <p className="text-slate-600 text-center py-4">No students enrolled in this batch</p>
                  ) : (
                    <div className="space-y-3">
                      <AnimatePresence>
                        {sessionData?.students?.map((item, index) => {
                          const subscriptionLabel = getSubscriptionLabel(item.subscription_status);
                          const isExpired = item.subscription_status === "expired";
                          
                          return (
                            <motion.div
                              key={item.student.user_id}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.03 }}
                              className={`p-4 rounded-2xl border-2 transition-all ${
                                item.is_marked
                                  ? item.attendance_status === "present"
                                    ? "bg-green-50 border-green-200"
                                    : "bg-red-50 border-red-200"
                                  : isExpired
                                    ? "bg-red-50/50 border-red-200"
                                    : "bg-slate-50 border-slate-200"
                              }`}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <p className="font-medium text-slate-900 text-sm line-clamp-2 leading-tight">{item.student.name}</p>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      {subscriptionLabel && (
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${subscriptionLabel.color}`}>
                                          {subscriptionLabel.text}
                                        </span>
                                      )}
                                      {item.is_marked && (
                                        <p className="text-xs text-slate-500">
                                          {item.attendance_status === "present" ? "Present" : "Absent"}
                                        </p>
                                      )}
                                      {rowErrors[item.student.user_id] && (
                                        <span
                                          data-testid={`row-error-${item.student.user_id}`}
                                          className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-100"
                                          title={rowErrors[item.student.user_id]}
                                        >
                                          Save failed · tap to retry
                                        </span>
                                      )}
                                    </div>
                                </div>

                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {item.is_marked && sessionData?.is_editable && (
                                    <Button
                                      onClick={() => undoAttendance(item.student.user_id)}
                                      size="sm"
                                      variant="ghost"
                                      className="h-10 w-10 rounded-full text-slate-400 hover:text-slate-600"
                                      title="Undo"
                                    >
                                      <Undo2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                  
                                  <Button
                                    onClick={() => markAttendance(item.student.user_id, "present")}
                                    disabled={marking || !sessionData?.is_editable || isExpired || (item.is_marked && item.attendance_status === "present")}
                                    title={isLocked ? "Locked — reopen session to edit." : undefined}
                                    className="h-10 w-10 sm:h-14 sm:w-14 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg active:scale-95 transition-transform disabled:opacity-50"
                                  >
                                    <CheckCircle2 className="w-4 h-4 sm:w-6 sm:h-6" />
                                  </Button>
                                  <Button
                                    onClick={() => markAttendance(item.student.user_id, "absent")}
                                    disabled={marking || !sessionData?.is_editable || isExpired || (item.is_marked && item.attendance_status === "absent")}
                                    className="h-10 w-10 sm:h-14 sm:w-14 rounded-full bg-gradient-to-br from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white shadow-lg active:scale-95 transition-transform disabled:opacity-50"
                                  >
                                    <XCircle className="w-4 h-4 sm:w-6 sm:h-6" />
                                  </Button>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </AnimatePresence>
                    </div>
                  )}
                </div>

                {/* Trial / Drop-in Students */}
                {sessionData?.trial_students?.length > 0 && (
                  <div className="bg-blue-50 rounded-2xl p-6 shadow-sm border border-blue-200">
                    <h3 className="text-lg font-bold font-heading text-slate-900 mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-600" />
                      Trial / Drop-in Students ({sessionData.trial_students.length})
                    </h3>
                    
                    <div className="space-y-3">
                      {sessionData.trial_students.map((item, index) => (
                        <motion.div
                          key={item.student.user_id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.03 }}
                          className={`p-4 rounded-2xl border-2 transition-all ${
                            item.is_marked
                              ? item.attendance_status === "present"
                                ? "bg-green-50 border-green-200"
                                : "bg-red-50 border-red-200"
                              : "bg-white border-slate-200"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-slate-900 text-sm line-clamp-2 leading-tight">{item.student.name}</p>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-600">
                                  Trial student
                                </span>
                                {item.is_marked && (
                                  <p className="text-xs text-slate-500">
                                    {item.attendance_status === "present" ? "Present" : "Absent"}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-1 flex-shrink-0">
                              {item.is_marked && sessionData?.is_editable && (
                                <Button
                                  onClick={() => undoAttendance(item.student.user_id, true)}
                                  size="sm"
                                  variant="ghost"
                                  className="h-10 w-10 rounded-full text-slate-400 hover:text-slate-600"
                                  title="Undo"
                                >
                                  <Undo2 className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                onClick={() => markAttendance(item.student.user_id, "present", true)}
                                disabled={marking || !sessionData?.is_editable || (item.is_marked && item.attendance_status === "present")}
                                className="h-10 w-10 sm:h-14 sm:w-14 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg active:scale-95 transition-transform disabled:opacity-50"
                              >
                                <CheckCircle2 className="w-4 h-4 sm:w-6 sm:h-6" />
                              </Button>
                              <Button
                                onClick={() => markAttendance(item.student.user_id, "absent", true)}
                                disabled={marking || !sessionData?.is_editable || (item.is_marked && item.attendance_status === "absent")}
                                className="h-10 w-10 sm:h-14 sm:w-14 rounded-full bg-gradient-to-br from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white shadow-lg active:scale-95 transition-transform disabled:opacity-50"
                              >
                                <XCircle className="w-4 h-4 sm:w-6 sm:h-6" />
                              </Button>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Cancel Session Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Ban className="w-5 h-5" />
              Cancel Class Session
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-red-50 rounded-xl border border-red-200">
              <p className="text-sm text-red-700">
                This will cancel the class and notify all enrolled students. This action cannot be undone.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Cancellation Reason *</Label>
              <select
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                className="w-full h-12 rounded-xl border px-4 bg-white"
              >
                <option value="">Select a reason</option>
                <option value="Instructor unavailable">Instructor unavailable</option>
                <option value="Studio unavailable">Studio unavailable</option>
                <option value="Low attendance">Low attendance</option>
                <option value="Weather conditions">Weather conditions</option>
                <option value="Public holiday">Public holiday</option>
                <option value="Technical issues">Technical issues</option>
                <option value="Other">Other</option>
              </select>
            </div>
            
            {cancellationReason === "Other" && (
              <div className="space-y-2">
                <Label>Specify reason</Label>
                <Input
                  placeholder="Enter reason"
                  onChange={(e) => setCancellationReason(e.target.value)}
                  className="h-12 rounded-xl"
                />
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setCancelDialogOpen(false)}
                className="flex-1 rounded-full"
              >
                Keep Class
              </Button>
              <Button
                onClick={cancelSession}
                disabled={!cancellationReason.trim()}
                className="flex-1 rounded-full bg-red-600 hover:bg-red-700"
              >
                Cancel Class
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Trial Student Dialog */}
      <Dialog open={trialDialogOpen} onOpenChange={(open) => { setTrialDialogOpen(open); if (!open) { setTrialStudentSearch(""); setTrialStudentId(""); } }}>
        <DialogContent className="max-w-md max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Add Trial / Drop-in
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
              <p className="text-sm text-blue-700">
                Trial students don't affect batch capacity or subscription class counts.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Search Student</Label>
              <Input
                data-testid="trial-student-search"
                placeholder="Type name or phone..."
                value={trialStudentSearch}
                onChange={(e) => { setTrialStudentSearch(e.target.value); setTrialStudentId(""); }}
                className="h-12 rounded-xl"
              />
              {trialStudentSearch.length > 0 && (
                <div className="max-h-48 overflow-y-auto border rounded-xl divide-y">
                  {availableTrialStudents
                    .filter(s => 
                      s.name?.toLowerCase().includes(trialStudentSearch.toLowerCase()) ||
                      s.phone?.includes(trialStudentSearch)
                    )
                    .map(student => (
                      <button
                        key={student.user_id}
                        data-testid={`trial-student-option-${student.user_id}`}
                        onClick={() => { setTrialStudentId(student.user_id); setTrialStudentSearch(student.name); }}
                        className={`w-full text-left px-4 py-3 text-sm hover:bg-blue-50 transition-colors ${
                          trialStudentId === student.user_id ? "bg-blue-50 font-medium" : ""
                        }`}
                      >
                        <span className="font-medium">{student.name}</span>
                        <span className="text-slate-500 ml-2">{student.phone}</span>
                      </button>
                    ))
                  }
                  {availableTrialStudents.filter(s => 
                    s.name?.toLowerCase().includes(trialStudentSearch.toLowerCase()) ||
                    s.phone?.includes(trialStudentSearch)
                  ).length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-3">No students found</p>
                  )}
                </div>
              )}
              {trialStudentId && (
                <p className="text-xs text-emerald-600">Selected: {trialStudentSearch}</p>
              )}
            </div>

            {/* Feb 2026 — Optional drop-in / trial payment recording */}
            <div className="space-y-3 border-t border-slate-200 pt-4">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-slate-700">Payment (optional)</Label>
                {studioTrialDefaultFee > 0 && (
                  <span className="text-xs text-slate-500">Default: ₹{studioTrialDefaultFee}</span>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Fee Amount</Label>
                <Input
                  data-testid="trial-fee-amount"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="0"
                  value={trialFee}
                  onChange={(e) => setTrialFee(e.target.value)}
                  className="h-11 rounded-xl"
                />
                <p className="text-[11px] text-slate-500">Leave blank or 0 to skip payment recording.</p>
              </div>

              {Number(trialFee) > 0 && (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Payment Status</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { key: "paid", label: "Paid" },
                        { key: "partial", label: "Partial" },
                        { key: "pending", label: "Pending" },
                      ].map((opt) => (
                        <button
                          key={opt.key}
                          type="button"
                          data-testid={`trial-payment-status-${opt.key}`}
                          onClick={() => setTrialPaymentStatus(opt.key)}
                          className={`h-10 rounded-xl text-sm font-medium border transition-colors ${
                            trialPaymentStatus === opt.key
                              ? "bg-blue-600 text-white border-blue-600"
                              : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {trialPaymentStatus === "partial" && (
                    <div className="space-y-1.5">
                      <Label className="text-xs">Amount Paid</Label>
                      <Input
                        data-testid="trial-amount-paid"
                        type="number"
                        min="0"
                        step="1"
                        placeholder="0"
                        value={trialAmountPaid}
                        onChange={(e) => setTrialAmountPaid(e.target.value)}
                        className="h-11 rounded-xl"
                      />
                      {trialFee && trialAmountPaid && Number(trialAmountPaid) < Number(trialFee) && (
                        <p className="text-[11px] text-amber-600">
                          Balance: ₹{Math.max(Number(trialFee) - Number(trialAmountPaid), 0).toFixed(2)}
                        </p>
                      )}
                    </div>
                  )}

                  {trialPaymentStatus !== "pending" && (
                    <div className="space-y-1.5">
                      <Label className="text-xs">Payment Method</Label>
                      <select
                        data-testid="trial-payment-method"
                        value={trialPaymentMethod}
                        onChange={(e) => setTrialPaymentMethod(e.target.value)}
                        className="w-full h-11 rounded-xl border border-slate-200 px-3 bg-white text-sm"
                      >
                        <option value="cash">Cash</option>
                        <option value="upi">UPI</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="card">Card</option>
                      </select>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <Label className="text-xs">Notes (optional)</Label>
                    <Input
                      data-testid="trial-notes"
                      placeholder="Reference no., remarks..."
                      value={trialNotes}
                      onChange={(e) => setTrialNotes(e.target.value)}
                      className="h-11 rounded-xl"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setTrialDialogOpen(false)}
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button
                data-testid="trial-add-submit"
                onClick={addTrialStudent}
                disabled={!trialStudentId || trialSubmitting}
                className="flex-1 rounded-full bg-blue-600 hover:bg-blue-700"
              >
                {trialSubmitting ? "Saving..." : "Add Student"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Substitute (Change Instructor) dialog */}
      <Dialog open={substituteOpen} onOpenChange={(v) => { setSubstituteOpen(v); if (!v) setSubstituteForm({ instructor_id: "", scope: "this_only", reason: "" }); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserCog className="w-5 h-5 text-violet-600" /> Change Instructor</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Instructor *</Label>
              <select
                value={substituteForm.instructor_id}
                onChange={(e) => setSubstituteForm({ ...substituteForm, instructor_id: e.target.value })}
                className="w-full h-10 rounded-xl border border-slate-200 px-3 bg-white text-sm"
                data-testid="substitute-instructor-select"
              >
                <option value="">— Select —</option>
                {allInstructors.filter(i => i.is_active !== false).map((i) => (
                  <option key={i.instructor_id} value={i.instructor_id}>{i.name}{i.role === "admin" ? "  •  Admin" : ""}{i.per_hour_rate ? ` · ₹${i.per_hour_rate}/hr` : ""}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Scope *</Label>
              <div className="space-y-2">
                <label className="flex items-start gap-2 cursor-pointer p-2 rounded-lg hover:bg-slate-50">
                  <input
                    type="radio"
                    name="sub-scope"
                    checked={substituteForm.scope === "this_only"}
                    onChange={() => setSubstituteForm({ ...substituteForm, scope: "this_only" })}
                    className="mt-0.5"
                  />
                  <div>
                    <p className="text-sm font-medium">This Instance Only</p>
                    <p className="text-xs text-slate-500">Just today's session. Substitute pickup.</p>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer p-2 rounded-lg hover:bg-slate-50">
                  <input
                    type="radio"
                    name="sub-scope"
                    checked={substituteForm.scope === "this_and_future"}
                    onChange={() => setSubstituteForm({ ...substituteForm, scope: "this_and_future" })}
                    className="mt-0.5"
                  />
                  <div>
                    <p className="text-sm font-medium">This & All Future Instances</p>
                    <p className="text-xs text-slate-500">Updates batch default + all future un-completed sessions. Past stays unchanged.</p>
                  </div>
                </label>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Reason (optional)</Label>
              <Input
                value={substituteForm.reason}
                onChange={(e) => setSubstituteForm({ ...substituteForm, reason: e.target.value })}
                placeholder="e.g. Riya unavailable, sick leave"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setSubstituteOpen(false)} className="flex-1 rounded-full">Cancel</Button>
              <Button onClick={submitSubstitute} className="flex-1 rounded-full bg-violet-600 hover:bg-violet-700 text-white" data-testid="substitute-confirm">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <BottomNav user={user} onLogout={onLogout} />
      {confirmDialogEl}

      {/* Receipt viewer for freshly-issued trial receipts (Feb 2026) */}
      <ReceiptViewerModal
        open={receiptViewerOpen}
        onOpenChange={(v) => {
          setReceiptViewerOpen(v);
          if (!v) setIssuedReceipt(null);
        }}
        receipt={issuedReceipt}
      />
    </div>
  );
};

export default AttendanceManagement;
